import { takeLatest, call, put, select } from 'redux-saga/effects'
import { getDateTime, getVersion, randomNumber } from '../../../utilities'
import { getUserSelector } from '../../user/selectors'
import { IUserState } from '../../user/store/user.types'
import { crudPcrException } from '../../pcr/store/pcr.actions'
import { PCRConstants } from '../../pcr/store/pcr.constants'
import { setNewPCR, setPcrId } from '../store/home.actions'
import { IPcr } from '../../pcr/store/pcr.types'
import { getCrewSelector } from '../../crew/selectors/getCrew.selector'
import { getPcrSelector } from '../selectors/getPcr.selector'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import Logger from '../../../services/logger.service'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { agencyNumberConfigToAgencyNumber } from '../../configurations/utils/configurations.util'
import { IPcrSummary } from '../store/home.types'
import { setTripInformationDefaults } from '../../tripInformation/saga/setTripInformationDefaults.saga'
import { setDateTimeConfig } from '../../times/saga/setDateTimeConfigs.saga'
import { isOnline } from '../../../utilities/isOnline.util'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { SubmitChartService } from '../../submitChart/services/submitChart.service'
import { IFetchPRIDResponse, ISubmitChartState } from '../../submitChart/store/submitChart.types'
import { setSubmitChartState } from '../../submitChart/store/submitChart.actions'
import { initialSubmitChartState } from '../../submitChart/store/submitChart.reducers'

function* fetchPridForNewPcr(pcrId: string) {
  if (!isOnline()) return null
  try {
    const authJwt: string = yield select(getAuthenticationJwtSelector)
    const submitChartService = new SubmitChartService(authJwt)
    const pridResponse: IFetchPRIDResponse = yield call([submitChartService, submitChartService.fetchPRID])
    if (pridResponse?.status === 200 && pridResponse.data?.prid) {
      return pridResponse.data.prid.toString()
    }
    Logger.warn(`PCR: ${pcrId} PRID fetch at creation failed — will retry on next open`)
  } catch (pridError) {
    Logger.warn(`PCR: ${pcrId} PRID fetch at creation threw — will retry on next open: ${pridError}`)
  }
  return null
}

export function* createPCR() {
  try {
    const forceCreateError = localStorage.getItem('FORCE_CREATE_ERROR')
    if (forceCreateError && forceCreateError === '1') {
      throw Error('Forcing create PCR error for testing purpose')
    }
    const user: IUserState = yield select(getUserSelector)
    const { serviceId, serviceName, userId } = user
    const pcrId: string = randomNumber().toString()
    const { vehicle } = yield select(getCrewSelector)
    const { agencyNumberConfig }: IConfigurationsState = yield select(getConfigurationsSelector)
    const agencyNumber = agencyNumberConfigToAgencyNumber(agencyNumberConfig)

    const pcr: IPcrSummary = yield select(getPcrSelector)
    const prid: string | null = yield call(fetchPridForNewPcr, pcrId)

    const newPCR: IPcrSummary = {
      ...pcr,
      serviceId,
      createdBy: userId,
      pcrId,
      agencyNumber,
      agencyName: serviceName,
      createDateTime: getDateTime(),
      version: getVersion(),
      unit: vehicle?.name || '',
      prid,
    }
    const pcrDocsService = new PcrDocsService(serviceId)

    yield call([pcrDocsService, pcrDocsService.createPcrDoc], newPCR)
    Logger.info(`PCR: ${pcrId} created by user: ${userId} in service: ${serviceId}`)

    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrId)
    if (!pcrDoc) {
      throw Error(`Something has gone wrong, Cannot find PCR with ID: ${pcrId} after creation`)
    }
    if (prid) {
      yield call([pcrDocsService, pcrDocsService.savePCRSilent], {
        ...pcrDoc,
        submitChart: { ...initialSubmitChartState, prid },
      })
    }

    yield put(setPcrId(pcrId))

    yield call(setDateTimeConfig)
    yield call(setTripInformationDefaults)

    if (prid) {
      yield put(setSubmitChartState({ ...initialSubmitChartState, prid } as ISubmitChartState))
    }

    yield put(setNewPCR(newPCR))
  } catch (error) {
    Logger.errorObj('Error in createPCR Saga', error)

    yield put(crudPcrException(`createPCR() error: ${error}`))
  }
}

export function* createPCRSaga() {
  yield takeLatest([PCRConstants.CREATE_PCR], performWithMutexHOG, createPCR)
}
